import React, { useEffect, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { FiCheckCircle, FiEdit, FiPlus } from "react-icons/fi";

import { API } from "../api";

import { toasts } from "../components/AppToast";


import Layout from "../components/Layout";

const EMPTY_FORM = {
  regclient_title: "",
  regclient_name: "",
  regclient_familyname: "",
  regclient_streetno: "",
  regclient_postcode: "",
  regclient_town: "",
  regclient_country: "",
  regclient_mobno: "",
  regclient_phoneno: "",
  regclient_email: "",
  status: "",
};

export default function AddRegularClient() {
  const navigate = useNavigate();
  const location = useLocation();

  // Edit data
  const regclientId = location.state?.regclient_id;
  const editClient = location.state?.client;
  const isEdit = Boolean(regclientId);

  const [form, setForm] = useState(EMPTY_FORM);

  const [countries, setCountries] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [seasonDiscounts, setSeasonDiscounts] = useState({});

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [seasonLoading, setSeasonLoading] = useState(false);

  // Auth
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login", {
        replace: true,
      });
    }
  }, [navigate]);

  // Countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await API.get("/countries");

        setCountries(res.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch countries:", error);

        toasts.error("Failed to load countries");
      }
    };

    fetchCountries();
  }, []);

  // Seasons
  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        setSeasonLoading(true);

        const res = await API.get("/seasons/with-durations");

        setSeasons(res.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch seasons:", error);

        toasts.error("Failed to load seasons");

        setSeasons([]);
      } finally {
        setSeasonLoading(false);
      }
    };

    fetchSeasons();
  }, []);

  // Load client data in edit mode
  useEffect(() => {
    if (!isEdit) {
      setForm(EMPTY_FORM);
      setSeasonDiscounts({});
      return;
    }

    if (!editClient) {
      toasts.error("Client information not found");

      navigate("/manage-regular-clients");

      return;
    }

    try {
      setLoading(true);

      const client = editClient;

      setForm({
        regclient_title:
          client.regclient_title != null
            ? String(client.regclient_title)
            : "",

        regclient_name:
          client.regclient_name != null
            ? String(client.regclient_name)
            : "",

        regclient_familyname:
          client.regclient_familyname != null
            ? String(client.regclient_familyname)
            : "",

        regclient_streetno:
          client.regclient_streetno != null
            ? String(client.regclient_streetno)
            : "",

        regclient_postcode:
          client.regclient_postcode != null
            ? String(client.regclient_postcode)
            : "",

        regclient_town:
          client.regclient_town != null
            ? String(client.regclient_town)
            : "",

        regclient_country:
          client.regclient_country != null
            ? String(client.regclient_country)
            : "",

        regclient_mobno:
          client.regclient_mobno != null
            ? String(client.regclient_mobno)
            : "",

        regclient_phoneno:
          client.regclient_phoneno != null
            ? String(client.regclient_phoneno)
            : "",

        regclient_email:
          client.regclient_email != null
            ? String(client.regclient_email)
            : "",

        status:
          client.status != null
            ? String(client.status)
            : "",
      });
    } catch (error) {
      console.error("Failed to set client data:", error);

      toasts.error("Failed to load client details");
    } finally {
      setLoading(false);
    }
  }, [isEdit, editClient, navigate]);

  // Load existing season discounts
  useEffect(() => {
    if (!isEdit || !regclientId) {
      return;
    }

    const loadDiscounts = async () => {
      try {
        const res = await API.get(
          `/regularclients/inner/${regclientId}`
        );

        const discountData = res.data?.data || [];

        const discountMap = {};

        if (Array.isArray(discountData)) {
          discountData.forEach((item) => {
            if (item.season_id != null) {
              discountMap[String(item.season_id)] =
                item.season_discount != null
                  ? String(item.season_discount)
                  : "0";
            }
          });
        }

        setSeasonDiscounts(discountMap);
      } catch (error) {
        console.error(
          "Failed to load season discounts:",
          error
        );

        toasts.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to load season discounts"
        );

        setSeasonDiscounts({});
      }
    };

    loadDiscounts();
  }, [isEdit, regclientId]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;

    // Mobile number: only 10 digits
    if (name === "regclient_mobno") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }

    // Phone number: only 10 digits
    if (name === "regclient_phoneno") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }

    // Post code: only 6 digits
    if (name === "regclient_postcode") {
      newValue = value.replace(/\D/g, "").slice(0, 6);
    }

    setForm((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  // Season discount change
  const handleSeasonDiscountChange = (seasonId, value) => {
    setSeasonDiscounts((prev) => ({
      ...prev,
      [String(seasonId)]: value,
    }));
  };

  // Submit form
  const handleSubmit = async () => {
    // Required field validation
    if (!form.regclient_title) {
      toasts.warning("Select title");
      return;
    }

    if (!form.regclient_name?.trim()) {
      toasts.warning("Enter client name");
      return;
    }

    if (!form.regclient_email?.trim()) {
      toasts.warning("Enter email address");
      return;
    }

    if (!form.regclient_country) {
      toasts.warning("Select country");
      return;
    }

    if (!form.status) {
      toasts.warning("Select status");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    if (!emailRegex.test(form.regclient_email.trim())) {
      toasts.warning("Enter a valid email address");
      return;
    }

    // Mobile number validation
    if (
      form.regclient_mobno &&
      !/^\d{10}$/.test(form.regclient_mobno)
    ) {
      toasts.warning(
        "Mobile No must contain exactly 10 digits"
      );
      return;
    }

    // Phone number validation
    if (
      form.regclient_phoneno &&
      !/^\d{10}$/.test(form.regclient_phoneno)
    ) {
      toasts.warning(
        "Phone No must contain exactly 10 digits"
      );
      return;
    }

    // Post code validation
    if (
      form.regclient_postcode &&
      !/^\d{6}$/.test(form.regclient_postcode)
    ) {
      toasts.warning(
        "Post Code must contain exactly 6 digits"
      );
      return;
    }

    // Save
    try {
      setSaving(true);

      const submitData = {
        ...form,

        season_discounts: seasons.map((season) => ({
          season_id: season.season_id,
          discount:
            seasonDiscounts[String(season.season_id)] ?? "0",
        })),
      };

      if (isEdit) {
        await API.put(
          `/regularclients/${regclientId}`,
          submitData
        );

        toasts.success("Client updated successfully");
      } else {
        await API.post(
          "/regularclients",
          submitData
        );

        toasts.success("Client added successfully");
      }

      setTimeout(() => {
        navigate("/manage-regular-clients");
      }, 500);
    } catch (error) {
      console.error("Submit Error:", error);

      console.error(
        "API ERROR:",
        error.response?.data
      );

      toasts.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Something went wrong while saving client"
      );
    } finally {
      setSaving(false);
    }
  };

  // Back
  const goBack = () => {
    if (saving) {
      return;
    }

    navigate("/manage-regular-clients");
  };

  return (
    <Layout activePage="Regular Clients Details">
      <main className="dashboard-main">
        <div className="user-form-page">

          {/* Header */}

          <div className="user-form-header">
            <div className="d-flex align-items-center gap-2">
              <div className="form-card-icon">
                {isEdit ? (
                  <FiEdit size={24} />
                ) : (
                  <FiPlus size={24} />
                )}
              </div>

              <div>
                <h2 className="um-modal-title mb-0">
                  {isEdit
                    ? "Edit Regular Client"
                    : "Add New Regular Client"}
                </h2>

                <p className="form-card-sub mb-0">
                  {isEdit
                    ? "(Update regular client information and seasonal discounts)"
                    : "(Create a new regular client and set seasonal discounts)"}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="um-btn-secondary"
              onClick={goBack}
              disabled={saving}
            >
              ← Back to Regular Clients
            </button>
          </div>

          {/* Form Card */}

          <div className="user-form-card">

            {/* Regular client information */}

            <div className="sec-div">
              <span>
                Regular Client Information
              </span>
            </div>

            <div className="row g-4">

              {/* Title */}

              <div className="col-xl-3 col-lg-3 col-md-6">
                <label className="um-label">
                  Title *
                </label>

                <select
                  className="um-select"
                  name="regclient_title"
                  value={form.regclient_title}
                  onChange={handleChange}
                >
                  <option value="">
                    — Select Title —
                  </option>

                  <option value="Mr">
                    Mr
                  </option>

                  <option value="Mrs">
                    Mrs
                  </option>

                  <option value="Ms">
                    Ms
                  </option>

                  <option value="Dr">
                    Dr
                  </option>
                </select>
              </div>

              {/* Client name */}

              <div className="col-xl-3 col-lg-3 col-md-6">
                <label className="um-label">
                  Client Name *
                </label>

                <input
                  className="um-input"
                  type="text"
                  name="regclient_name"
                  placeholder="Enter client name"
                  value={form.regclient_name}
                  onChange={handleChange}
                />
              </div>

              {/* Family name */}

              <div className="col-xl-3 col-lg-3 col-md-6">
                <label className="um-label">
                  Family Name
                </label>

                <input
                  className="um-input"
                  type="text"
                  name="regclient_familyname"
                  placeholder="Enter family name"
                  value={form.regclient_familyname}
                  onChange={handleChange}
                />
              </div>

              {/* Street number */}

              <div className="col-xl-3 col-lg-3 col-md-6">
                <label className="um-label">
                  Street Number
                </label>

                <input
                  className="um-input"
                  type="text"
                  name="regclient_streetno"
                  placeholder="Enter street number"
                  value={form.regclient_streetno}
                  onChange={handleChange}
                />
              </div>

              {/* Post code */}

              <div className="col-xl-3 col-lg-3 col-md-6">
                <label className="um-label">
                  Post Code
                </label>

                <input
                  className="um-input"
                  type="text"
                  name="regclient_postcode"
                  placeholder="Enter 6 digit post code"
                  inputMode="numeric"
                  maxLength={6}
                  value={form.regclient_postcode}
                  onChange={handleChange}
                />

               
              </div>

              {/* Town */}

              <div className="col-xl-3 col-lg-3 col-md-6">
                <label className="um-label">
                  Town
                </label>

                <input
                  className="um-input"
                  type="text"
                  name="regclient_town"
                  placeholder="Enter town"
                  value={form.regclient_town}
                  onChange={handleChange}
                />
              </div>

              {/* Country */}

              <div className="col-xl-3 col-lg-3 col-md-6">
                <label className="um-label">
                  Country *
                </label>

                <select
                  className="um-select"
                  name="regclient_country"
                  value={form.regclient_country}
                  onChange={handleChange}
                >
                  <option value="">
                    — Select Country —
                  </option>

                  {countries.map((country) => (
                    <option
                      key={country.country_id}
                      value={country.country_id}
                    >
                      {country.country_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mobile number */}

              <div className="col-xl-3 col-lg-3 col-md-6">
                <label className="um-label">
                  Mobile No
                </label>

                <input
                  className="um-input"
                  type="tel"
                  name="regclient_mobno"
                  placeholder="Enter 10 digit mobile number"
                  inputMode="numeric"
                  maxLength={10}
                  value={form.regclient_mobno}
                  onChange={handleChange}
                />

             
              </div>

              {/* Phone number */}

              <div className="col-xl-3 col-lg-3 col-md-6">
                <label className="um-label">
                  Phone No
                </label>

                <input
                  className="um-input"
                  type="tel"
                  name="regclient_phoneno"
                  placeholder="Enter 10 digit phone number"
                  inputMode="numeric"
                  maxLength={10}
                  value={form.regclient_phoneno}
                  onChange={handleChange}
                />

              
              </div>

              {/* Email */}

              <div className="col-xl-3 col-lg-3 col-md-6">
                <label className="um-label">
                  Email Address *
                </label>

                <input
                  className="um-input"
                  type="email"
                  name="regclient_email"
                  placeholder="Enter email address"
                  value={form.regclient_email}
                  onChange={handleChange}
                />
              </div>

              {/* Status */}

              <div className="col-xl-3 col-lg-3 col-md-6">
                <label className="um-label">
                  Status *
                </label>

                <select
                  className="um-select"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                >
                  <option value="">
                    — Select Status —
                  </option>

                  <option value="1">
                    Active
                  </option>

                  <option value="2">
                    Inactive
                  </option>
                </select>
              </div>
            </div>

            {/* Season discounts */}

            <div className="sec-div mt-4 mb-3">
              <span>
                Season Wise Per Day Price Discount
                <i className="ms-1">
                  (Discount in %)
                </i>
              </span>
            </div>

            {/* Season table */}

            <div className="season-discount-card">
              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle mb-0">

                  <thead className="table-light um-table-head">
                    <tr>
                      <th
                        scope="col"
                        style={{ width: "70px" }}
                      >
                        #
                      </th>

                      <th scope="col">
                        Season Name
                      </th>

                      <th
                        scope="col"
                        style={{ width: "250px" }}
                      >
                        Discount
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {seasonLoading || loading ? (
                      Array.from({
                        length: 4,
                      }).map((_, index) => (
                        <tr key={index}>
                          <td>
                            <div className="placeholder-glow">
                              <span className="placeholder col-6" />
                            </div>
                          </td>

                          <td>
                            <div className="placeholder-glow">
                              <span className="placeholder col-8" />
                            </div>
                          </td>

                          <td>
                            <div className="placeholder-glow">
                              <span className="placeholder col-8" />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : seasons.length > 0 ? (
                      seasons.map((season, index) => {
                        const seasonId =
                          String(season.season_id);

                        const discount =
                          seasonDiscounts[seasonId] ??
                          "0";

                        return (
                          <tr key={seasonId}>

                            {/* Number */}

                            <td>
                              <strong>
                                {String(index + 1).padStart(
                                  2,
                                  "0"
                                )}
                              </strong>
                            </td>

                            {/* Season name */}

                            <td>
                              <div>
                                <div className="fw-semibold">
                                  {season.season_name ||
                                    "—"}
                                </div>

                                {season.duration && (
                                  <small className="text-muted">
                                    {season.duration}
                                  </small>
                                )}
                              </div>
                            </td>

                            {/* Discount */}

                            <td>
                              <div className="input-group">
                                <input
                                  type="number"
                                  className="form-control"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  value={discount}
                                  onChange={(e) =>
                                    handleSeasonDiscountChange(
                                      seasonId,
                                      e.target.value
                                    )
                                  }
                                />

                                <span className="input-group-text">
                                  %
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={3}
                          className="text-center py-5"
                        >
                          <div className="fs-2 mb-2">
                            📅
                          </div>

                          <div className="fw-semibold">
                            No seasons found
                          </div>

                          <div className="text-muted">
                            No seasonal discount information is available.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>

                </table>
              </div>
            </div>

            {/* Footer */}

            <div className="user-form-footer">

              <button
                type="button"
                className="um-btn-secondary"
                onClick={goBack}
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="button"
                className="um-btn-primary"
                onClick={handleSubmit}
                disabled={saving || loading}
              >
                {saving ? (
                  <>
                    <span className="spinner" />

                    {isEdit
                      ? "Updating..."
                      : "Saving..."}
                  </>
                ) : isEdit ? (
                  <>
                    <FiCheckCircle />

                    Update Client
                  </>
                ) : (
                  <>
                    <FiPlus />

                    Add Client
                  </>
                )}
              </button>

            </div>
          </div>
        </div>
      </main>

    </Layout>
  );
}
